import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import dayjs from "dayjs"
import { Button, Box, Stack } from "@mui/material"
import GenericHeaderReturnButton from "../../../components/GenericHeaderReturnButton"
import PackagingExecutionHeader from "../PackagingExecutionHeader"
import PackagingExecutionResume from "../packagingExecutionToValidate/PackagingExecutionResume"
import PackagingExecutionPresentation from "./PackagingExecutionPresentation"
import PackagingExecutionSectionsAndWeights from "./PackagingExecutionSectionsAndWeights"
import PackagingByBrandAndTypeTable from "../PackagingByBrandAndTypeTable"
import PackagingExecutionReprodInfo from "../packagingExecutionToValidate/PackagingExecutionReprodInfo"
import { setPackagingExecutionGazTest, showPackagingExecutionRead, setPackagingExecutionToPackagingCounting, setPackagingExecutionPackagingTimes } from "../../../actions/PackagingExecution/packagingExecution"
import { ACTIVE_KFC_BRANDS } from "../../../utils"
import { PE_STATUSES } from "../../../utils/packagingExecutions"
import { setPackagingExecutionToInProgress } from "../../../actions/PackagingExecution/packagingExecution"
import PackagingExecutionImagePreview from "./PackagingExecutionImagePreview"
import { getFitCornerTag } from "../../../utils/productTagsUtils"
import PackagingExecutionFooter from "../PackagingExecutionFooter"
import ModalGazTest from "./ModalGazTest"
import PEBlockHead from "./PEBlockHead"
import PEPackagings from "./PEPackagings"
import { getOrderedPEPackagingsWithNoTypes, getPECurrentActiveStep, getPEStepperOptions, orderedPackagingTypes } from "../../../utils/packagingExecutionUtils"
import { TESTGAZ_STATUS, computeTestStatus, groupTestsByThreshold, hasCappedPackaging } from "../../../utils/testGaz"
import PEStepper from "../PEStepper"
import PEGazTest from "./PEGazTest"

const getButtonText = (status, nextStep) => {
	if (status === PE_STATUSES.todo) return "DÉMARRER"
	if (status === PE_STATUSES.inProgress) {
		if (nextStep) {
			if (!orderedPackagingTypes.includes(nextStep.value)) return "PASSER AU COMPTAGE DES BARQUETTES"
			return `PASSER AU ${nextStep.label.toUpperCase()}`
		}

		return "PASSER À LA RECETTE SUIVANTE"
	}
	return ""
}

/**
 * Determine gas test UI behavior for current packaging execution.
 * Controls button visibility, variant, and main CTA disabled state.
 */
export const getGazUIState = (packagingExecution, gazTests) => {
  const { status, gazTest = [], packagings } = packagingExecution
  const currentStep = getPECurrentActiveStep(status, packagings)

  // Defaults
  let showGazButton = false
  let gazButtonVariant = "outlined"
  let disableMainCTA = false

  // ---- CASE 1: PE in TODO ----
  if (status === PE_STATUSES.todo) {
    const beforeStartTests = gazTest.filter(test => test.step === "BEFORE_START")
    const hasSuccess = beforeStartTests.some(test => computeTestStatus(test) === TESTGAZ_STATUS.SUCCESS)

    if (hasCappedPackaging(packagingExecution)) showGazButton = true
    gazButtonVariant = hasSuccess ? "outlined" : "contained"
    disableMainCTA = hasCappedPackaging(packagingExecution) ? !hasSuccess : false
  }

  // ---- CASE 2: PE in PROGRESS ----
  if (status === PE_STATUSES.inProgress) {
		if (currentStep === "CAPPED") {
			const grouped = groupTestsByThreshold(gazTests)

			// Get the tests of the latest threshold (ex: "Barquettage operculé n°1800")
			const latestTests = Array.from(grouped.entries())
				.filter(([key]) => key.startsWith("Barquettage operculé n°"))
				.sort((a, b) => parseInt(a[0].match(/\d+/), 10) - parseInt(b[0].match(/\d+/), 10))
				.at(-1)?.[1] || []

			const hasSuccess = !latestTests.length || latestTests.some(test => computeTestStatus(test) === TESTGAZ_STATUS.SUCCESS)

			showGazButton = true
			gazButtonVariant = hasSuccess ? "outlined" : "contained"
			disableMainCTA = !hasSuccess
		} else {
			showGazButton = false
			disableMainCTA = false
		}
  }

  // Hide gaz button for waste or disposable step
  if (currentStep === "WASTE_TO_WEIGHT") showGazButton = false
  return { showGazButton, gazButtonVariant, disableMainCTA }
}

const PackagingExecutionTodo = ({ packagingExecution, selectedSite, selectedDate, hasSubStatus, gazTests = [] }) => {
	const dispatch = useDispatch()
	const [openGazTestModal, setOpenGazTestModal] = useState(false)

	const goToPackagingExecutionRead = () => {dispatch(showPackagingExecutionRead(selectedSite.objectId, selectedDate))}
	const currentStep = getPECurrentActiveStep(packagingExecution.status, packagingExecution.packagings)
	const allStepperOptions = getPEStepperOptions(packagingExecution.packagings)
	const hasReproductions = packagingExecution.sections.some(section => Array.isArray(section.reproductions) && section.reproductions.length > 0)
	const hasOngoingReproductions = packagingExecution.sections.some(section => (section.reproductions || []).some(reprod => reprod.status === "IN_PROGRESS"))
	const currentStatusIndex = allStepperOptions.findIndex(step => step.value === currentStep)
	const nextStep = allStepperOptions[currentStatusIndex + 1]

	const { showGazButton, gazButtonVariant, disableMainCTA } = getGazUIState(packagingExecution, gazTests)

	const [selectedPackaging, setSelectedPackaging] = useState(null)
	const [imageModalOpen, setImageModalOpen] = useState(false)


	const _savePackagingExecutionToInProgress = useCallback(() => {
		dispatch(setPackagingExecutionToInProgress(packagingExecution.objectId))
	}, [packagingExecution])

	const _saveGazTest = useCallback((values) => {
		dispatch(setPackagingExecutionGazTest(packagingExecution.objectId, values))
	}, [packagingExecution])

	useEffect(() => {
		if (packagingExecution.status === PE_STATUSES.inProgress) {
			const firstPackagingTodo = (packagingExecution.packagings || []).find(packaging => !packaging.hasOwnProperty("realNumber"))
			setSelectedPackaging(firstPackagingTodo)
		}
	}, [packagingExecution])

	const action = useMemo(() => {
		return () => {
			// Always save the current packaging endTime when main button is clicked
			dispatch(setPackagingExecutionPackagingTimes(packagingExecution.objectId, currentStep, nextStep, packagingExecution.status))

			if (packagingExecution.status === PE_STATUSES.todo) {
				_savePackagingExecutionToInProgress()
				return
			}

			if (packagingExecution.status === PE_STATUSES.inProgress && !selectedPackaging) {
				return
			}

			if (packagingExecution.status === PE_STATUSES.inProgress && nextStep.value === PE_STATUSES.packagingsCounting) {
				dispatch(setPackagingExecutionToPackagingCounting(packagingExecution.objectId, currentStep))
				return
			}
		}
	}, [packagingExecution, currentStep, nextStep, selectedPackaging])

	return (
		<>
			<PackagingExecutionHeader
				leftAction={<GenericHeaderReturnButton handleClick={goToPackagingExecutionRead} />}
				subtitle={`${selectedSite.name} - ${dayjs(selectedDate).format("DD/MM/YYYY")}`}
				fixedHeader
			/>
			<Box sx={{ p: 3, marginTop: "65px" }}>
				<Stack direction="row" spacing={3}>
					<div className="flex1">
						<PackagingExecutionResume
							recipeLabel={packagingExecution.recipeName}
							status={packagingExecution.status}
							packagingLine={packagingExecution.packagingLine?.name}
							expectedPackagingNumber={packagingExecution.expectedPackagingNumber}
							uniqueCode={packagingExecution.uniqueCode}
							hasSubStatus={hasSubStatus}
							fitCornerTag={getFitCornerTag(packagingExecution.recipe)}
						/>
					</div>
				</Stack>
				{hasReproductions && (
					<PackagingExecutionReprodInfo
						text={
							hasOngoingReproductions
								? "Une reprod a été demandée pour atteindre la quantité prévue."
								: "La reprod a bien été intégrée à vos quantités."
						}
					/>
				)}
			</Box>
			<PEStepper
				packagings={packagingExecution.packagings}
				status={packagingExecution.status}
				withTitle={false}
				times={{
					theoreticalStartTime: packagingExecution.theoreticalStartTime,
					theoreticalEndTime: packagingExecution.theoreticalEndTime,
					startTime: packagingExecution.startTime,
					endTime: packagingExecution.endTime
				}}
			/>
			<Box sx={{ px: 3, pb: 3, pt: 4 }}>
				<Stack spacing={5} sx={{ marginTop: "3px" }}>
					<Stack spacing={3}>
						<PEBlockHead
							icon="call-bell"
							title="Dressage"
							description="Dressez votre barquette en suivant les quantités et les instructions de dressage."
						/>
						<Stack display="flex" direction="row" spacing={2} width="100%" alignItems="stretch">
							<Box sx={{ flex: 1, display: "flex", "stretchSelf": 1 }}>
								<PackagingExecutionSectionsAndWeights
									sections={packagingExecution.sections}
								/>
							</Box>
							{(selectedPackaging || packagingExecution.packagings[0]) && (
								<PackagingExecutionPresentation
									packaging={selectedPackaging || packagingExecution.packagings[0]}
									openImagePreview={() => setImageModalOpen(true)}
								/>
							)}
						</Stack>
					</Stack>
					<Stack spacing={2} sx={{ marginTop: "48px !important" }}>
						<PEBlockHead title="Barquettes" icon="chart" />
						<PEPackagings packagings={getOrderedPEPackagingsWithNoTypes(packagingExecution.packagings)} />
					</Stack>
					<div>
						<PackagingByBrandAndTypeTable
							packagings={packagingExecution.packagings}
							brands={ACTIVE_KFC_BRANDS}
						/>
					</div>
					{hasCappedPackaging(packagingExecution) && gazTests.length > 0 && (
						<Stack spacing={2} sx={{ marginTop: "48px !important" }}>
							<PEGazTest
								gazTests={gazTests}
								PEDone={false}
							/>
						</Stack>
					)}
				</Stack>
			</Box>
			{(selectedPackaging || packagingExecution.packagings[0]) && (
				<PackagingExecutionImagePreview
					packaging={selectedPackaging || packagingExecution.packagings[0]}
					open={imageModalOpen}
					onClose={() => setImageModalOpen(false)}
				/>
			)}
			<PackagingExecutionFooter
				fixed
				rightAction={
					<Stack direction="row" spacing={2}>
						{showGazButton && (
							<Button
								variant={gazButtonVariant}
								onClick={() => setOpenGazTestModal(true)}
							>
								FAIRE LE TEST GAZ
							</Button>
						)}
						<Button
							variant="contained"
							disabled={disableMainCTA}
							onClick={action}
						>
							{getButtonText(packagingExecution.status, nextStep)}
						</Button>
					</Stack>
				}
			/>
			<ModalGazTest open={openGazTestModal} onSave={_saveGazTest} onClose={() => setOpenGazTestModal(false)} currentStep={currentStep} packagingExecution={packagingExecution} />
		</>
	)
}

export default PackagingExecutionTodo